import {
  ActionList,
  Banner,
  Bleed,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormLayout,
  Icon,
  InlineGrid,
  InlineStack,
  LegacyCard,
  LegacyStack,
  Popover,
  Tag,
  Text,
  Tooltip,
} from '@shopify/polaris'
import { ArrowDownIcon, ArrowUpIcon, DeleteIcon } from '@shopify/polaris-icons'
import { getIn, useFormikContext } from 'formik'
import { useCallback, useMemo, useState } from 'react'
import ReactQuill from 'react-quill'
import { SWRResponse } from 'swr'

import { SellingPlanAnchor, SellingPlanPreAnchorBehavior } from '../common/anchor-models'
import {
  AnchorsEditModal,
  anchorTypeSellingPlanIntervals,
  cutoffOptions,
  cutoffWindowTooltip,
  firstDeliveryTooltip,
  preAnchorBehaviorOptions,
} from '../common/AnchorsEditModal'
import { backendFetchResult, Result } from '../common/api'
import { ConfirmDialog, useConfirmDialogState } from '../common/ConfirmDialog'
import {
  gidToId,
  MemoedSelect,
  MemoedTextField,
  pluralize,
  ResultToastOrBanner,
  sortAnchors,
  useDialogActivator,
} from '../common/helpers'
import { HelpIcon, HelpLabel, LegacyHelpIcon } from '../common/HelpIcon'
import { MemoedOptionalNumericSelect } from '../common/NumericSelect'
import {
  SellingPlanInterval,
  SellingPlanPricingPolicyAdjustmentType,
  SellingPlanType,
} from '../common/plan-models'
import { useShopCurrencySymbol } from '../common/Price'
import {
  PriceAdjustmentTypeField,
  PriceAdjustmentValueField,
  SecondaryPriceAdjustmentTypeField,
} from '../common/PriceAdjustmentFields'
import quillStyles from '../common/quillEditor.module.css'
import {
  IntervalCountField,
  IntervalField,
  MaxCyclesField,
  MaxCyclesPrepaidField,
  MinCyclesField,
} from '../common/ScheduleFields'
import { useSessionStorage } from '../common/session-storage'
import { useShopShopifySettingsSWR } from '../common/shopify-settings'
import { FinishingBehaviorSelect } from '../common/subscription-fields'
import * as urls from '../common/urls'
import { anchorLabel } from '../subscription-page/ScheduleCard'
import { SellingPlanWorkflows } from './models-common'
import {
  defaultSellingPlanLabel,
  defaultSellingPlanName,
  initialSellingPlanFormData,
  SellingPlanGroupFormData,
  sellingPlanTypeFields,
} from './models-form'
import { PrePaidDeliveriesField } from './PrePaidDeliveriesField'
import { sellingPlanDeleteWarning } from './SellingPlanPage'
import { WorkflowActionsModal } from './WorkflowActionsModal'

const sellingPlanTypeLabels: Record<SellingPlanType, string> = {
  basic: 'Ongoing subscription',
  'basic-trial': 'Free trial → ongoing subscription',
  prepaid: 'Prepaid',
  'specific-dates': 'Specific dates',
  advanced: 'Custom/Advanced',
}

const sellingPlanTypeOptions: { label: string; value: SellingPlanType }[] = Object.keys(
  sellingPlanTypeLabels
).map((planType) => ({
  label: sellingPlanTypeLabels[planType as SellingPlanType],
  value: planType as SellingPlanType,
}))

export const VariantCard = ({
  index,
  sellingPlanWorkflowsSWR,
}: {
  index: number
  sellingPlanWorkflowsSWR: SWRResponse<SellingPlanWorkflows | null>
}) => {
  const currencySymbol = useShopCurrencySymbol()
  const confirmDialogState = useConfirmDialogState()
  const removeSellingPlanConfirmDialogState = useConfirmDialogState()
  const editAnchorsActivator = useDialogActivator()
  const workflowActionsActivator = useDialogActivator()
  const [isPrePaidOrdersBannerDismissed, setPrePaidOrdersBannerDismissed] =
    useSessionStorage<boolean>('pre-paid-orders-banner-dismissed', false)

  const { data: shopShopifySettingsData } = useShopShopifySettingsSWR()

  const formik = useFormikContext<SellingPlanGroupFormData>()

  const relatedWorkflow = useMemo(
    () => sellingPlanWorkflowsSWR.data?.[formik.values.sellingPlans[index].id ?? ''] ?? null,
    [index, formik.values.sellingPlans, sellingPlanWorkflowsSWR.data]
  )
  const relatedWorkflowId = relatedWorkflow?.id ?? null
  const [removeRelatedWorkflowResult, setRemoveRelatedWorkflowResult] =
    useState<Result<any> | null>(null)

  const { setFieldValue, setFieldError } = formik
  const handleChange = useCallback(
    (value: any, id: string) => {
      setFieldValue(id, value)
      setFieldError(id, undefined)
    },
    [setFieldValue, setFieldError]
  )
  const handleNumberFromStringChange = useCallback(
    (value: any, id: string) => {
      setFieldValue(id, value ? Number(value) : null)
      setFieldError(id, undefined)
    },
    [setFieldValue, setFieldError]
  )
  const handleTrialLengthChange = useCallback(
    (value: string, id: string) => {
      const parsed = value === '' ? undefined : value === 'default' ? null : Number(value)
      setFieldValue(id, parsed)
      setFieldError(id, undefined)
    },
    [setFieldValue, setFieldError]
  )

  const getError = useCallback(
    (field: string) => getIn(formik.touched, field) && getIn(formik.errors, field),
    [formik.touched, formik.errors]
  )

  const [addSellingPlanPopoverActive, setAddSellingPlanPopoverActive] = useState(false)

  const toggleAddSellingPlanPopoverActive = useCallback(
    () =>
      setAddSellingPlanPopoverActive((addSellingPlanPopoverActive) => !addSellingPlanPopoverActive),
    []
  )

  const handleRemovingSellingPlan = useCallback(
    (index: number) => {
      const planId = formik.values.sellingPlans[index].id

      const plans = [...(formik.values.sellingPlans || [])]
      plans.splice(index, 1)
      formik.setFieldValue('sellingPlans', plans)

      if (planId) {
        const plansToDelete = [...(formik.values.sellingPlansToDelete || [])]
        plansToDelete.push(planId)
        formik.setFieldValue('sellingPlansToDelete', plansToDelete)
      }
    },
    [formik]
  )

  const handleAddSellingPlan = useCallback(
    (index: number, type: SellingPlanType) => {
      const plans = [...(formik.values.sellingPlans || [])]
      plans.splice(index, 0, initialSellingPlanFormData(type))
      formik.setFieldValue('sellingPlans', plans)
      setAddSellingPlanPopoverActive(false)
    },
    [formik]
  )

  const handleMoveUpSellingPlan = useCallback(() => {
    const plans = [...(formik.values.sellingPlans || [])]
    const deleted = plans.splice(index, 1)
    plans.splice(index - 1, 0, deleted[0])
    formik.setFieldValue('sellingPlans', plans)
  }, [formik, index])

  const handleMoveDownSellingPlan = useCallback(() => {
    const plans = [...(formik.values.sellingPlans || [])]
    const deleted = plans.splice(index, 1)
    plans.splice(index + 1, 0, deleted[0])
    formik.setFieldValue('sellingPlans', plans)
  }, [formik, index])

  const onTypeChange = useCallback(
    (type: SellingPlanType) => {
      formik.setFieldValue(`sellingPlans[${index}].type`, type)
      formik.setFieldValue(
        `sellingPlans[${index}].trialLengthDays`,
        type === 'basic-trial' ? undefined : null
      )

      if (
        type !== 'basic-trial' &&
        formik.values.sellingPlans[index].recurringPricingPolicy?.adjustmentType ===
          SellingPlanPricingPolicyAdjustmentType.NONE
      ) {
        formik.setFieldValue(
          `sellingPlans[${index}].recurringPricingPolicy.adjustmentType`,
          SellingPlanPricingPolicyAdjustmentType.PERCENTAGE
        )
        formik.setFieldValue(`sellingPlans[${index}].recurringPricingPolicy.adjustmentValue`, '0')
      }

      if (type === 'advanced') {
        return
      }

      if (type === 'specific-dates') {
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.minCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.maxCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].finishingBehavior`, 'immediately')
        formik.setFieldValue(`sellingPlans[${index}].prePaidDeliveries`, 1)
        formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.intervalCount`, 1)
        if (formik.values.sellingPlans[index].deliveryPolicy.recurring.interval === 'DAY') {
          formik.setFieldValue(
            `sellingPlans[${index}].deliveryPolicy.recurring.interval`,
            SellingPlanInterval.YEAR
          )
        }
        return
      }

      if (type === 'prepaid') {
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.minCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.maxCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].finishingBehavior`, 'end-of-cycle')
        formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.anchors`, [])

        return
      }

      if (type === 'basic') {
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.minCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.maxCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].finishingBehavior`, 'immediately')
        formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.anchors`, [])
        formik.setFieldValue(`sellingPlans[${index}].prePaidDeliveries`, 1)
      }

      // Reset recurring pricing policy if switching to 'basic' and both recurring
      // and fixed pricing policies are set to 'basic-trial' initial values
      const recurringPricingPolicy = formik.values.sellingPlans[index].recurringPricingPolicy
      if (
        type === 'basic' &&
        recurringPricingPolicy != null &&
        recurringPricingPolicy.adjustmentType ===
          SellingPlanPricingPolicyAdjustmentType.PERCENTAGE &&
        parseFloat(recurringPricingPolicy.adjustmentValue) === 0 &&
        formik.values.sellingPlans[index].fixedPricingPolicy.adjustmentType ===
          SellingPlanPricingPolicyAdjustmentType.PRICE &&
        parseFloat(formik.values.sellingPlans[index].fixedPricingPolicy.adjustmentValue) === 0
      ) {
        formik.setFieldValue(`sellingPlans[${index}].recurringPricingPolicy`, null)
        formik.setFieldValue(
          `sellingPlans[${index}].fixedPricingPolicy.adjustmentType`,
          SellingPlanPricingPolicyAdjustmentType.NONE
        )
        formik.setFieldValue(`sellingPlans[${index}].fixedPricingPolicy.adjustmentValue`, '0')
      }

      if (type === 'basic-trial') {
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.minCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].billingPolicy.recurring.maxCycles`, null)
        formik.setFieldValue(`sellingPlans[${index}].finishingBehavior`, 'immediately')
        formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.anchors`, [])
        formik.setFieldValue(`sellingPlans[${index}].prePaidDeliveries`, 1)
        formik.setFieldValue(
          `sellingPlans[${index}].fixedPricingPolicy.adjustmentType`,
          SellingPlanPricingPolicyAdjustmentType.PRICE
        )
        formik.setFieldValue(`sellingPlans[${index}].fixedPricingPolicy.adjustmentValue`, '0')
        formik.setFieldValue(`sellingPlans[${index}].recurringPricingPolicy`, {
          adjustmentType: SellingPlanPricingPolicyAdjustmentType.PERCENTAGE,
          adjustmentValue: '0',
          afterCycle: 1,
        })
      }
    },
    [formik, index]
  )

  const handleAnchorsUpdate = useCallback(
    (
      anchors: SellingPlanAnchor[],
      preAnchorBehavior?: SellingPlanPreAnchorBehavior | undefined,
      cutoff?: number | null
    ) => {
      formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.anchors`, anchors)
      formik.setFieldValue(
        `sellingPlans[${index}].deliveryPolicy.recurring.preAnchorBehavior`,
        preAnchorBehavior
      )
      formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.cutoff`, cutoff)

      if (anchors.length === 0) {
        return
      }

      formik.setFieldValue(
        `sellingPlans[${index}].deliveryPolicy.recurring.interval`,
        anchorTypeSellingPlanIntervals[anchors[0].type]
      )

      if (anchors.length > 1) {
        formik.setFieldValue(`sellingPlans[${index}].deliveryPolicy.recurring.intervalCount`, 1)
      }
    },
    [formik, index]
  )

  const handleRemoveRecurringPricingPolicy = useCallback(
    async (index: number) => {
      formik.setFieldValue(`sellingPlans[${index}].recurringPricingPolicy`, null)

      if (relatedWorkflowId) {
        const result = await backendFetchResult('DELETE', `/workflows/${relatedWorkflowId}`)
        setRemoveRelatedWorkflowResult(result)
        sellingPlanWorkflowsSWR.mutate()
      }
    },
    [formik, relatedWorkflowId, sellingPlanWorkflowsSWR]
  )

  const planGid = formik.values.sellingPlans[index].id
  const planTitle = 'Selling Plan' + (planGid ? ` #${gidToId(planGid)}` : '')
  const type = formik.values.sellingPlans[index].type
  const anchors = sortAnchors(formik.values.sellingPlans[index].deliveryPolicy.recurring.anchors)
  const isDailyInterval =
    formik.values.sellingPlans[index].deliveryPolicy.recurring.interval === SellingPlanInterval.DAY

  const deliveryInterval = formik.values.sellingPlans[index].deliveryPolicy.recurring.interval
  const deliveryIntervalCount =
    formik.values.sellingPlans[index].deliveryPolicy.recurring.intervalCount
  const prePaidDeliveries = formik.values.sellingPlans[index].prePaidDeliveries
  const trialLengthOptions = useMemo(() => {
    const defaultPeriodCount = (deliveryIntervalCount || 1) * (prePaidDeliveries || 1)
    const defaultUnit = pluralize(defaultPeriodCount, deliveryInterval.toLowerCase())
    return [
      { label: `One billing period (${defaultPeriodCount} ${defaultUnit})`, value: 'default' },
      ...Array.from({ length: 60 }, (_, i) => {
        const n = i + 1
        return { label: `${n} ${pluralize(n, 'day')}`, value: String(n) }
      }),
    ]
  }, [deliveryInterval, deliveryIntervalCount, prePaidDeliveries])

  return (
    <>
      <LegacyCard sectioned>
        <LegacyStack vertical spacing="loose">
          <InlineStack gap="300" align="space-between" blockAlign="start" wrap={false}>
            <InlineStack gap="300" blockAlign="center" wrap>
              <Text variant="headingMd" as="h2">
                {planTitle}
              </Text>
              <MemoedSelect
                name={`sellingPlans[${index}].type`}
                id={`sellingPlans[${index}].type`}
                label="Type"
                labelHidden
                options={sellingPlanTypeOptions}
                value={formik.values.sellingPlans[index].type}
                onChange={onTypeChange}
              />
            </InlineStack>
            <InlineStack blockAlign="center" gap="200" wrap={false}>
              <ButtonGroup variant="segmented">
                {index !== 0 && (
                  <Tooltip content="Move up" dismissOnMouseOut>
                    <Button
                      accessibilityLabel="Move up"
                      icon={<Icon source={ArrowUpIcon} tone="base" />}
                      onClick={handleMoveUpSellingPlan}
                    />
                  </Tooltip>
                )}
                {index !== formik.values.sellingPlans.length - 1 && (
                  <Tooltip content="Move down" dismissOnMouseOut>
                    <Button
                      accessibilityLabel="Move down"
                      icon={<Icon source={ArrowDownIcon} tone="base" />}
                      onClick={handleMoveDownSellingPlan}
                    />
                  </Tooltip>
                )}
              </ButtonGroup>
              <Button
                onClick={confirmDialogState.show}
                icon={DeleteIcon}
                variant="primary"
                tone="critical"
              />
            </InlineStack>
          </InlineStack>
          <FormLayout>
            {shopShopifySettingsData?.shopUsingThirdPartyFulfillmentServices &&
              formik.values.sellingPlans[index].prePaidDeliveries > 1 &&
              !isPrePaidOrdersBannerDismissed && (
                <Banner tone="info" onDismiss={() => setPrePaidOrdersBannerDismissed(true)}>
                  <p>
                    Prepaid orders in Shopify may require you to alter settings in your fulfillment
                    provider. We recommend running tests to ensure scheduled fulfillments can be
                    processed by your provider. Please reach out to your fullfillment provider for
                    more details.
                  </p>
                </Banner>
              )}
            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="500">
              <MemoedTextField
                name={`sellingPlans[${index}].name`}
                id={`sellingPlans[${index}].name`}
                type="text"
                label={
                  <HelpLabel tooltip="Selling plan name displayed in the cart and during checkout. It's recommended that it includes the frequency of deliveries">
                    Subscription plan name
                  </HelpLabel>
                }
                placeholder={defaultSellingPlanName(
                  formik.values.name,
                  formik.values.sellingPlans[index].deliveryPolicy.recurring.interval,
                  formik.values.sellingPlans[index].deliveryPolicy.recurring.intervalCount,
                  formik.values.sellingPlans[index].type,
                  formik.values.sellingPlans[index].prePaidDeliveries
                )}
                value={formik.values.sellingPlans[index].name}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                error={getError(`sellingPlans[${index}].name`)}
              />
              <MemoedTextField
                name={`sellingPlans[${index}].planSelectorLabel`}
                id={`sellingPlans[${index}].planSelectorLabel`}
                type="text"
                placeholder={defaultSellingPlanLabel(
                  formik.values.sellingPlans[index].deliveryPolicy.recurring.interval,
                  formik.values.sellingPlans[index].deliveryPolicy.recurring.intervalCount,
                  formik.values.sellingPlans[index].deliveryPolicy.recurring.anchors,
                  formik.values.sellingPlans[index].type,
                  formik.values.sellingPlans[index].prePaidDeliveries,
                  formik.values.sellingPlans[index].trialLengthDays
                )}
                label={
                  <HelpLabel tooltip="Short label displayed in the plan selector on the product page">
                    Subscription selection display label
                  </HelpLabel>
                }
                value={formik.values.sellingPlans[index].planSelectorLabel}
                onChange={handleChange}
                onBlur={formik.handleBlur}
                autoComplete="off"
                error={getError(`sellingPlans[${index}].planSelectorLabel`)}
              />
            </InlineGrid>
            {(sellingPlanTypeFields(type).interval || sellingPlanTypeFields(type).prepaid) && (
              <FormLayout.Group>
                <LegacyStack distribution="fillEvenly" spacing="loose">
                  <LegacyStack.Item fill>
                    {sellingPlanTypeFields(type).interval && (
                      <LegacyStack distribution="fillEvenly">
                        {anchors.length <= 1 && (
                          <IntervalCountField
                            name={`sellingPlans[${index}].deliveryPolicy.recurring.intervalCount`}
                            id={`sellingPlans[${index}].deliveryPolicy.recurring.intervalCount`}
                            label="Delivery frequency"
                            onChange={handleChange}
                            value={
                              formik.values.sellingPlans[index].deliveryPolicy.recurring
                                .intervalCount
                            }
                          />
                        )}
                        <LegacyStack alignment="trailing">
                          <LegacyStack.Item fill>
                            <IntervalField
                              name={`sellingPlans[${index}].deliveryPolicy.recurring.interval`}
                              id={`sellingPlans[${index}].deliveryPolicy.recurring.interval`}
                              label={anchors.length > 1 ? 'Delivery frequency' : '  '}
                              onChange={(val: SellingPlanInterval, id: string) => {
                                handleChange(val, id)
                                handleChange(
                                  [],
                                  `sellingPlans[${index}].deliveryPolicy.recurring.anchors`
                                )
                              }}
                              value={
                                formik.values.sellingPlans[index].deliveryPolicy.recurring.interval
                              }
                              hasMultipleAnchors={anchors.length > 1}
                            />
                          </LegacyStack.Item>
                        </LegacyStack>
                      </LegacyStack>
                    )}
                  </LegacyStack.Item>
                  {sellingPlanTypeFields(type).prepaid && (
                    <LegacyStack distribution="fillEvenly">
                      {sellingPlanTypeFields(type).prepaid && (
                        <PrePaidDeliveriesField
                          value={formik.values.sellingPlans[index].prePaidDeliveries}
                          onChange={(v) =>
                            formik.setFieldValue(`sellingPlans[${index}].prePaidDeliveries`, v)
                          }
                          pagePath={urls.planUrl(formik.values.id)}
                        />
                      )}
                    </LegacyStack>
                  )}
                </LegacyStack>
              </FormLayout.Group>
            )}
            {anchors.length > 0 && (
              <LegacyStack distribution="fillEvenly" spacing="loose">
                <BlockStack gap="300">
                  <InlineStack>
                    <LegacyStack.Item fill>
                      <Text as="span">Delivery anchors</Text>
                    </LegacyStack.Item>
                    <Button onClick={editAnchorsActivator.show} variant="plain">
                      Edit delivery anchors
                    </Button>
                  </InlineStack>
                  <InlineStack wrap gap="100">
                    {anchors.map((anchor, i) => (
                      <Tag key={i}>{anchorLabel(anchor)}</Tag>
                    ))}
                  </InlineStack>
                </BlockStack>
                <LegacyStack distribution="fillEvenly">
                  <MemoedSelect
                    name={`sellingPlans[${index}].deliveryPolicy.recurring.preAnchorBehavior`}
                    id={`sellingPlans[${index}].deliveryPolicy.recurring.preAnchorBehavior`}
                    label={<HelpLabel tooltip={firstDeliveryTooltip}>First Delivery</HelpLabel>}
                    options={preAnchorBehaviorOptions}
                    onChange={handleChange}
                    value={
                      formik.values.sellingPlans[index].deliveryPolicy.recurring.preAnchorBehavior
                    }
                  />
                  <MemoedOptionalNumericSelect
                    name={`sellingPlans[${index}].deliveryPolicy.recurring.cutoff`}
                    id={`sellingPlans[${index}].deliveryPolicy.recurring.cutoff`}
                    label={<HelpLabel tooltip={cutoffWindowTooltip}>Cutoff window</HelpLabel>}
                    options={cutoffOptions}
                    onChange={handleNumberFromStringChange}
                    value={formik.values.sellingPlans[index].deliveryPolicy.recurring.cutoff}
                  />
                </LegacyStack>
              </LegacyStack>
            )}
            {sellingPlanTypeFields(type).anchors && anchors.length === 0 && !isDailyInterval && (
              <FormLayout.Group>
                <Button onClick={editAnchorsActivator.show} variant="primary">
                  Add delivery anchors
                </Button>
              </FormLayout.Group>
            )}
            {sellingPlanTypeFields(type).minMaxCyclesAndFinishingBehavior && (
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="500">
                {type !== 'prepaid' && (
                  <LegacyStack distribution="fillEvenly">
                    <MinCyclesField
                      name={`sellingPlans[${index}].billingPolicy.recurring.minCycles`}
                      id={`sellingPlans[${index}].billingPolicy.recurring.minCycles`}
                      onChange={handleNumberFromStringChange}
                      value={formik.values.sellingPlans[index].billingPolicy.recurring.minCycles}
                    />
                    <MaxCyclesField
                      name={`sellingPlans[${index}].billingPolicy.recurring.maxCycles`}
                      id={`sellingPlans[${index}].billingPolicy.recurring.maxCycles`}
                      onChange={handleNumberFromStringChange}
                      value={formik.values.sellingPlans[index].billingPolicy.recurring.maxCycles}
                    />
                  </LegacyStack>
                )}
                {type === 'prepaid' && (
                  <LegacyStack distribution="fillEvenly">
                    <MaxCyclesPrepaidField
                      name={`sellingPlans[${index}].billingPolicy.recurring.maxCycles`}
                      id={`sellingPlans[${index}].billingPolicy.recurring.maxCycles`}
                      onChange={handleNumberFromStringChange}
                      value={formik.values.sellingPlans[index].billingPolicy.recurring.maxCycles}
                    />
                  </LegacyStack>
                )}
                <FinishingBehaviorSelect
                  name={`sellingPlans[${index}].finishingBehavior`}
                  id={`sellingPlans[${index}].finishingBehavior`}
                  onChange={handleChange as (val: string, id: string) => any}
                  value={formik.values.sellingPlans[index].finishingBehavior}
                />
              </InlineGrid>
            )}
            {type !== 'basic-trial' && (
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="500">
                <PriceAdjustmentTypeField
                  name={`sellingPlans[${index}].fixedPricingPolicy.adjustmentType`}
                  id={`sellingPlans[${index}].fixedPricingPolicy.adjustmentType`}
                  onChange={handleChange}
                  value={formik.values.sellingPlans[index].fixedPricingPolicy.adjustmentType}
                />
                <PriceAdjustmentValueField
                  name={`sellingPlans[${index}].fixedPricingPolicy.adjustmentValue`}
                  id={`sellingPlans[${index}].fixedPricingPolicy.adjustmentValue`}
                  prePaidDeliveries={formik.values.sellingPlans[index].prePaidDeliveries}
                  currencySymbol={currencySymbol}
                  adjustmentType={
                    formik.values.sellingPlans[index].fixedPricingPolicy.adjustmentType
                  }
                  value={formik.values.sellingPlans[index].fixedPricingPolicy.adjustmentValue}
                  error={getError(`sellingPlans[${index}].fixedPricingPolicy.adjustmentValue`)}
                  onChange={handleChange}
                  onBlur={formik.handleBlur}
                />
              </InlineGrid>
            )}
            {formik.values.sellingPlans[index].recurringPricingPolicy && (
              <Bleed marginInline={type === 'basic-trial' ? '0' : '200'}>
                <Box
                  background={type === 'basic-trial' ? undefined : 'bg-fill-secondary'}
                  borderRadius="200"
                  padding={type === 'basic-trial' ? '0' : '200'}
                >
                  <BlockStack gap="300">
                    <InlineGrid columns={{ xs: 1, sm: 2 }} gap="500">
                      {type !== 'basic-trial' && (
                        <Text variant="headingSm" as="h3">
                          Future price change
                          <LegacyHelpIcon>
                            Price adjustment applied to the base product price after a specific
                            cycle
                          </LegacyHelpIcon>
                        </Text>
                      )}
                      {type === 'basic-trial' && (
                        <MemoedSelect
                          name={`sellingPlans[${index}].trialLengthDays`}
                          id={`sellingPlans[${index}].trialLengthDays`}
                          label={
                            <HelpLabel tooltip="Number of free trial days before the first charge">
                              Trial length
                            </HelpLabel>
                          }
                          options={trialLengthOptions}
                          placeholder="Choose a trial length"
                          value={
                            formik.values.sellingPlans[index].trialLengthDays === undefined
                              ? ''
                              : formik.values.sellingPlans[index].trialLengthDays === null
                              ? 'default'
                              : String(formik.values.sellingPlans[index].trialLengthDays)
                          }
                          onChange={handleTrialLengthChange}
                          error={getError(`sellingPlans[${index}].trialLengthDays`)}
                        />
                      )}
                      <InlineStack align="end" blockAlign="start">
                        {sellingPlanWorkflowsSWR.data && (
                          <ButtonGroup>
                            <InlineStack blockAlign="center" gap="100">
                              <Button
                                onClick={workflowActionsActivator.show}
                                variant="plain"
                                disabled={!planGid}
                              >
                                {planGid && sellingPlanWorkflowsSWR.data[planGid]
                                  ? type === 'basic-trial'
                                    ? 'Edit post trial actions'
                                    : 'Edit additional actions'
                                  : type === 'basic-trial'
                                  ? 'Add post trial actions'
                                  : 'Add additional actions'}
                              </Button>
                              <HelpIcon>
                                {type === 'basic-trial'
                                  ? 'Additional actions applied after checkout'
                                  : 'Additional actions applied when the future price change occurs.'}
                              </HelpIcon>
                            </InlineStack>
                            {type !== 'basic-trial' && (
                              <>
                                <Button
                                  onClick={() =>
                                    planGid && sellingPlanWorkflowsSWR.data?.[planGid]
                                      ? removeSellingPlanConfirmDialogState.show()
                                      : handleRemoveRecurringPricingPolicy(index)
                                  }
                                  variant="plain"
                                  tone="critical"
                                >
                                  Remove
                                </Button>
                                <ConfirmDialog
                                  state={removeSellingPlanConfirmDialogState}
                                  destructive
                                  onConfirm={() => handleRemoveRecurringPricingPolicy(index)}
                                  message="Removing the future price change will also remove the additional actions. This action is irreversible."
                                  confirmButtonTitle="Remove"
                                  cancelButtonTitle="Cancel"
                                />
                              </>
                            )}
                          </ButtonGroup>
                        )}
                      </InlineStack>
                    </InlineGrid>
                    <InlineGrid columns={{ xs: 1, sm: type === 'basic-trial' ? 2 : 3 }} gap="500">
                      <SecondaryPriceAdjustmentTypeField
                        name={`sellingPlans[${index}].recurringPricingPolicy.adjustmentType`}
                        id={`sellingPlans[${index}].recurringPricingPolicy.adjustmentType`}
                        onChange={handleChange}
                        value={
                          formik.values.sellingPlans[index].recurringPricingPolicy!.adjustmentType
                        }
                        freeTrial={type === 'basic-trial'}
                      />
                      <PriceAdjustmentValueField
                        name={`sellingPlans[${index}].recurringPricingPolicy.adjustmentValue`}
                        id={`sellingPlans[${index}].recurringPricingPolicy.adjustmentValue`}
                        prePaidDeliveries={formik.values.sellingPlans[index].prePaidDeliveries}
                        currencySymbol={currencySymbol}
                        adjustmentType={
                          formik.values.sellingPlans[index].recurringPricingPolicy!.adjustmentType
                        }
                        value={
                          formik.values.sellingPlans[index].recurringPricingPolicy!.adjustmentValue
                        }
                        error={getError(
                          `sellingPlans[${index}].recurringPricingPolicy.adjustmentValue`
                        )}
                        onChange={handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {type !== 'basic-trial' && (
                        <MemoedTextField
                          name={`sellingPlans[${index}].recurringPricingPolicy.afterCycle`}
                          id={`sellingPlans[${index}].recurringPricingPolicy.afterCycle`}
                          type="number"
                          label={
                            <HelpLabel tooltip="Select 1 if you want to apply the adjustment right after checkout.">
                              After cycle
                            </HelpLabel>
                          }
                          min="1"
                          autoComplete="off"
                          value={String(
                            formik.values.sellingPlans[index].recurringPricingPolicy!.afterCycle
                          )}
                          onChange={handleNumberFromStringChange}
                          onBlur={formik.handleBlur}
                          error={getError(
                            `sellingPlans[${index}].recurringPricingPolicy.afterCycle`
                          )}
                        />
                      )}
                    </InlineGrid>
                  </BlockStack>
                </Box>
              </Bleed>
            )}
            {!formik.values.sellingPlans[index].recurringPricingPolicy && (
              <InlineStack blockAlign="center" gap="100">
                <Button
                  onClick={() =>
                    formik.setFieldValue(`sellingPlans[${index}].recurringPricingPolicy`, {
                      adjustmentType: SellingPlanPricingPolicyAdjustmentType.PERCENTAGE,
                      adjustmentValue: '0',
                      afterCycle: 1,
                    })
                  }
                  variant="plain"
                >
                  Add future price change
                </Button>
                <HelpIcon>Price adjustment applied after a specific cycle</HelpIcon>
              </InlineStack>
            )}
            <BlockStack gap="100">
              <InlineStack align="space-between">
                <HelpLabel tooltip="Detailed commitment description displayed on the product page when the plan is selected (optional)">
                  Description
                </HelpLabel>
                <Checkbox
                  label="Visual Editor"
                  checked={formik.values.sellingPlans[index].richEditor}
                  onChange={(checked) => handleChange(checked, `sellingPlans[${index}].richEditor`)}
                />
              </InlineStack>
              {formik.values.sellingPlans[index].richEditor && (
                <ReactQuill
                  value={formik.values.sellingPlans[index].description}
                  onChange={(value) => handleChange(value, `sellingPlans[${index}].description`)}
                  className={quillStyles.quillEditor}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['clean'],
                    ],
                    clipboard: {
                      matchVisual: false,
                    },
                  }}
                  preserveWhitespace
                />
              )}
              {!formik.values.sellingPlans[index].richEditor && (
                <MemoedTextField
                  name={`sellingPlans[${index}].description`}
                  id={`sellingPlans[${index}].description`}
                  type="text"
                  label="Description"
                  labelHidden
                  placeholder=""
                  multiline={3}
                  value={formik.values.sellingPlans[index].description}
                  onChange={handleChange}
                  autoComplete="off"
                />
              )}
            </BlockStack>
          </FormLayout>
        </LegacyStack>
      </LegacyCard>

      <InlineStack align="start">
        <Popover
          active={addSellingPlanPopoverActive}
          activator={
            <div style={{ margin: '12px' }}>
              <Button
                disclosure={addSellingPlanPopoverActive ? 'up' : 'down'}
                onClick={toggleAddSellingPlanPopoverActive}
                variant="plain"
              >
                {index === formik.values.sellingPlans.length - 1
                  ? 'Add selling plan'
                  : 'Insert selling plan'}
              </Button>
            </div>
          }
          onClose={toggleAddSellingPlanPopoverActive}
        >
          <ActionList
            actionRole="menuitem"
            items={Object.keys(sellingPlanTypeLabels).map((planType) => ({
              content: sellingPlanTypeLabels[planType as SellingPlanType],
              onAction: () => handleAddSellingPlan(index + 1, planType as SellingPlanType),
            }))}
          />
        </Popover>
      </InlineStack>

      <AnchorsEditModal
        activator={editAnchorsActivator}
        anchors={formik.values.sellingPlans[index].deliveryPolicy.recurring.anchors}
        interval={formik.values.sellingPlans[index].deliveryPolicy.recurring.interval}
        preAnchorBehavior={
          formik.values.sellingPlans[index].deliveryPolicy.recurring.preAnchorBehavior
        }
        cutoff={formik.values.sellingPlans[index].deliveryPolicy.recurring.cutoff}
        onChange={handleAnchorsUpdate}
        pagePath={urls.planUrl(formik.values.id)}
      />

      <ConfirmDialog
        state={confirmDialogState}
        destructive
        onConfirm={() => handleRemovingSellingPlan(index)}
        message={sellingPlanDeleteWarning}
        confirmButtonTitle={'Remove'}
      />
      <ResultToastOrBanner
        result={removeRelatedWorkflowResult}
        setResult={setRemoveRelatedWorkflowResult}
      />
      <WorkflowActionsModal
        open={workflowActionsActivator.open}
        onClose={workflowActionsActivator.close}
        workflow={relatedWorkflow}
        sellingPlanId={gidToId(planGid ?? '')}
        onSaved={() => sellingPlanWorkflowsSWR.mutate()}
      />
    </>
  )
}
